import { Box } from '@mui/material'
import { useAuth } from '../../../context/AuthContext'
import { useLocation, useOutletContext } from 'react-router-dom'
import { useEffect, useState, useMemo, useCallback } from 'react'
import { editObject, getObject, addObject, getObjectsWithFilter } from '../../../api/apiClientCrud'
import { toast } from 'react-toastify'
import useNavigate from '../../hooks/useNavigate'
import dayjs from 'dayjs'
import Form from '../../form/Form'
import Toolbar from '../../toolbar/Toolbar'
import { useTranslation } from 'react-i18next'
import { getTaxFields } from './AccountingFields'
import useSaveEntity from '../../hooks/useSaveEntity'
import { useFetchEntities } from '../../hooks/useFetchEntities'
import {
  OTHER_RATE,
  OTHER_RATE_ACRE,
  SELLING_RATE,
  SELLING_RATE_ACRE,
  SERVICE_RATE,
  SERVICE_RATE_ACRE,
  TRAINING_RATE
} from '../../../utils/constants/constants'

const ROUTES = {
  EDIT: '/tax/edit',
  VIEW: '/tax/view',
  ADD: '/tax/add'
}

const buildPayload = ({ data, user, mode }) => {
  const common = {
    periodYear: dayjs(data.date).format('YYYY'),
    periodMonth: dayjs(data.date).format('MM'),
    periodYearMonth: dayjs(data.date).format('YYYYMM')
  }

  return mode === 'edit'
    ? { ...data, ...common, lastModBy: user.email }
    : { ...data, ...common, company_id: user.company_id, createdBy: user.email }
}

function Tax() {
  const location = useLocation()
  const { user } = useAuth()
  const userRight = useOutletContext()
  const [company, setCompany] = useState({})
  const [editTax, setEditTax] = useState({})
  const [isSubmit, setIsSubmit] = useState(false)
  const history = useNavigate()
  const path = location.pathname
  const taxId = location.state?.id
  const { t } = useTranslation()

  const mode = useMemo(() => {
    if (path === ROUTES.EDIT) return 'edit'
    if (path === ROUTES.VIEW) return 'view'
    if (path === ROUTES.ADD) return 'add'
    return null
  }, [path])

  const taxQuery = mode === 'edit' || mode === 'view' ? { key: 'tax', fn: getObject, args: ['tax', taxId] } : null

  const queries = [{ key: 'company', fn: getObject, args: ['company', user.company_id] }, taxQuery].filter(Boolean)
  const { data: fetched, loading: loadingEntities } = useFetchEntities(queries)

  const { saveEntity, loading } = useSaveEntity({
    entityName: 'tax',
    mode,
    id: taxId,
    user,
    args: [editTax],
    addFn: addObject,
    editFn: editObject,
    buildPayload,
    onSuccess: async () => {},
    onError: () => toast.error('Erreur lors de la sauvegarde', { autoClose: 2000 }),
    onSuccessRedirect: (id) => history(location.state?.history || ROUTES.VIEW, { state: { id } })
  })

  const toolbar = useMemo(
    () => ({
      accessRight: userRight,
      title: () => {
        if (path.includes('/add')) return 'Nouvelle Cotisation URSSAF'

        const periodYearMonth = editTax.periodYearMonth
        return periodYearMonth ? `Cotisation URSSAF ${periodYearMonth}` : 'Cotisation URSSAF'
      },
      objectType: 'tax',
      actions: { edit: true, files: true, back: true, audits: true, list: true }
    }),
    [userRight, editTax]
  )

  const fields = useMemo(() => getTaxFields(), [])

  const options = useMemo(
    () => ({
      fields,
      data: editTax,
      location
    }),
    [fields, editTax, location]
  )

  const calcul = useCallback(
    (ca_selling, ca_other, ca_service) => {
      let tax_service = 0
      let tax_selling = 0
      let tax_other = 0
      let tax_training = 0
      const ca = (ca_other || 0) + (ca_selling || 0) + (ca_service || 0)
      let tax_total = 0

      const isAcreActive = company.is_acre && dayjs(company.acre_end).isAfter(dayjs())

      if (isAcreActive) {
        tax_service = (SERVICE_RATE_ACRE * ca_service) / 100
        tax_selling = (SELLING_RATE_ACRE * ca_selling) / 100
        tax_other = (OTHER_RATE_ACRE * ca_other) / 100
      } else {
        tax_service = (SERVICE_RATE * ca_service) / 100
        tax_selling = (SELLING_RATE * ca_selling) / 100
        tax_other = (OTHER_RATE * ca_other) / 100
      }

      tax_training = (ca * TRAINING_RATE) / 100
      tax_total = tax_training + tax_service + tax_selling + tax_other

      const dateValConv = t('dates.shortDate', { date: dayjs(editTax.date).toDate() })
      const safeRound = (num) => Math.round(num + Number.EPSILON)
      setEditTax((prevTax) => ({
        ...prevTax,
        description: `Cotisations ${dateValConv}`,
        tax_training: safeRound(tax_training),
        tax_service: safeRound(tax_service),
        tax_selling: safeRound(tax_selling),
        tax_other: safeRound(tax_other),
        CA_selling: ca_selling,
        CA_service: ca_service,
        CA_other: ca_other,
        CA_total: ca,
        tax_total: safeRound(tax_total)
      }))
    },
    [company, editTax.date]
  )

  useEffect(() => {
    const getInvoicesLines = async () => {
      const period = dayjs(editTax.date).format('YYYYMM')

      try {
        const [resService, resSelling, resOther] = await Promise.all([
          getObjectsWithFilter(
            'invoices',
            {
              company_id: user.company_id,
              periodYearMonth: period,
              status: { $in: ['80', '81'] }
            },
            { type: 2 }
          ),
          getObjectsWithFilter(
            'invoices',
            {
              company_id: user.company_id,
              periodYearMonth: period,
              status: { $in: ['80', '81'] }
            },
            { type: 1 }
          ),
          getObjectsWithFilter(
            'invoices',
            {
              company_id: user.company_id,
              periodYearMonth: period,
              status: { $in: ['80', '81'] }
            },
            { type: 3 }
          )
        ])

        const ca_service = resService?.data?.totalAmount || 0
        const ca_selling = resSelling?.data?.totalAmount || 0
        const ca_other = resOther?.data?.totalAmount || 0

        calcul(ca_selling, ca_other, ca_service)
      } catch {
        toast.error('Erreur lors du chargement', { autoClose: 2000 })
      }
    }

    if (editTax.date) {
      getInvoicesLines()
    }
  }, [editTax.date, user.company_id, calcul])

  useEffect(() => {
    if (!loadingEntities) {
      if (fetched?.tax) setEditTax(fetched.tax)
      if (fetched?.company) setCompany(fetched.company)
    }
  }, [fetched, loadingEntities])

  useEffect(() => {
    if (isSubmit) {
      saveEntity({ data: editTax })
      setIsSubmit(false)
    }
  }, [isSubmit, editTax, saveEntity])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flexBasis: 'auto',
        flexGrow: 1,
        flexShrink: 1
      }}
    >
      <Toolbar options={toolbar} object={editTax} />
      <Form mode={mode} options={options} setDataParent={setEditTax} setIsSubmitParent={setIsSubmit} />
    </Box>
  )
}

export default Tax
